import { useEffect } from 'react'

import { IconProps } from 'components_new/atoms/Icon'

import { useTranslation } from 'language/translator'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import {
  useCustomer,
  useIsManagementCompany,
  useAllowInternalFollowUp
} from 'redux/hooks/Customer'
import { useAllowAccessApiKeys } from 'redux/hooks/Permissions'
import {
  useHasConfigurableIntegrations,
  useIntegrationsFetched
} from 'redux/hooks/Integrations'
import { useIntegrationActions } from 'redux/actions/Integrations'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { isMcpEnabledForCustomer } from 'helpers/Functions/isMcpEnabled'

export const useSettingItems = (): {
  body: string
  disabled?: boolean
  disabledTitle?: string
  icon: IconProps
  href: string
  name: string
  title: string
  hidden?: boolean
}[] => {
  const { t } = useTranslation()

  const customer = useCustomer()
  const { isAdmin, isEconomist } = useAccountSettings()
  const isManagementCompany = useIsManagementCompany()
  const allowAccessApiKeys = useAllowAccessApiKeys()
  const allowInternalFollowUp = useAllowInternalFollowUp()
  const hasConfigurableIntegrations = useHasConfigurableIntegrations()
  const integrationsFetched = useIntegrationsFetched()
  const { tryGetIntegrations } = useIntegrationActions()

  useEffect(() => {
    if (!integrationsFetched) {
      tryGetIntegrations()
    }
  }, [integrationsFetched])

  const hasOnlyEconomy = isEconomist && !isAdmin
  const hasSSO = customer?.allow_access.sso
  const hasWhiteLabel =
    customer?.allow_access.white_label_colors ||
    customer?.allow_access.white_label_logotype

  const navItems = [
    {
      body: t('customization', 'page_body'),
      disabled: !hasWhiteLabel || hasOnlyEconomy,
      disabledTitle: !hasWhiteLabel
        ? t('global', 'not_in_license')
        : t('global', 'no_access'),
      icon: { name: 'PaletteOutlined' as const },
      href: '/settings/customization',
      name: t('global', 'customization'),
      title: t('customization', 'page_title')
    },
    {
      body: t('users', 'page_body'),
      disabled: hasOnlyEconomy,
      disabledTitle: t('global', 'no_access'),
      icon: { name: 'Users' as const },
      href: '/settings/users',
      name: t('global', 'users'),
      title: t('users', 'page_title')
    },
    {
      body: t('data_access', 'page_body'),
      disabled: hasOnlyEconomy,
      disabledTitle: t('global', 'no_access'),
      icon: { name: 'Access' as const },
      href: '/settings/data-access',
      name: t('global', 'data_access'),
      title: t('data_access', 'page_title'),
      hidden: !allowInternalFollowUp
    },
    {
      body: t('api_keys', 'page_body'),
      icon: { name: 'VpnKeyOutlined' as const },
      href: '/settings/api-keys',
      name: t('global', 'api_keys'),
      title: t('api_keys', 'page_title'),
      hidden: !allowAccessApiKeys
    },
    {
      body: t('mcp_tokens', 'landing_body'),
      icon: { name: 'AI' as const },
      href: '/settings/mcp',
      name: t('mcp_tokens', 'landing_name'),
      title: t('mcp_tokens', 'landing_title'),
      hidden: !isMcpEnabledForCustomer(customer)
    },
    {
      body: t('sso', 'page_body'),
      disabled: !hasSSO || hasOnlyEconomy,
      disabledTitle: !hasSSO
        ? t('global', 'not_in_license')
        : t('global', 'no_access'),
      icon: { name: 'VerifiedUserOutlined' as const },
      href: '/settings/authentication',
      name: t('global', 'sso'),
      title: t('sso', 'page_title')
    },
    {
      body: t('companies', 'page_body'),
      disabled: hasOnlyEconomy,
      disabledTitle: t('global', 'no_access'),
      icon: { name: 'ApartmentOutlined' as const },
      href: '/settings/companies',
      name: isManagementCompany
        ? `${t('global', 'company_groups')} ${t('global', 'and', true)} ${t(
          'global',
          'companies',
          true
        )}`
        : t('global', 'companies'),
      title: `${t('global', 'manage')}${
        isManagementCompany
          ? ' ' +
            t('global', 'company_groups', true) +
            ' ' +
            t('global', 'and', true)
          : ''
      } ${t('global', 'companies', true)}`
    },
    {
      body: t('economy', 'page_body'),
      icon: { name: 'PaidOutlined' as const },
      href: '/settings/economy',
      name: t('global', 'economy'),
      title: t('economy', 'page_title')
    },
    {
      body: t('configuration', 'page_body'),
      disabled: hasOnlyEconomy,
      disabledTitle: t('global', 'no_access'),
      icon: { name: 'TuneOutlined' as const },
      href: '/settings/config',
      name: t('global', 'config'),
      title: t('configuration', 'page_title')
    },
    {
      body: t('integrations', 'page_body'),
      disabled: hasOnlyEconomy,
      disabledTitle: t('global', 'no_access'),
      icon: { name: 'ApiOutlined' as const },
      href: '/settings/integrations',
      name: t('global', 'integrations'),
      title: t('integrations', 'page_title'),
      hidden: !hasConfigurableIntegrations
    }
  ]

  return navItems
    .filter((item) => !item.hidden)
    .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
}
